import type { ReactNode } from 'react'
import { useCoachingStore } from '../store'
import { OverallComment } from './overall-comment'
import { RankingBanner } from './ranking-banner'
import type { ReviewBodyProps } from './review-tab'
import { AlternativeApproaches } from './sections/alternative-approaches'
import { CodeSection } from './sections/code-section'
import { ComplexitySection } from './sections/complexity-section'
import { CodingPacingSection } from './sections/pacing-section'
import { PostMortemSection } from './sections/post-mortem-section'
import { VerificationSection } from './sections/verification-section'

/**
 * Coding review body (spec §8.5) — the per-type `coding` entry in
 * `review-tab.tsx`'s `BODY_BY_TYPE` map. Owns the shared sections (ranking
 * banner, post-mortem, overall — same components A4 built for
 * `DefaultReviewBody`) plus the coding-only sections in spec §8.5's order:
 * code, complexity, pacing, verification, alternative approach, post-mortem,
 * overall.
 * Pacing is the
 * coding-only `CodingPacingSection` (LG-632): the stored duration is the only
 * pacing fact a coding submission holds, so the shared benchmark layout —
 * whose base behavior system-design keeps — never renders here.
 *
 * Alternative approach reads the catalog (LG-640): the approaches authored for
 * this review's own problem, with their real solution code and recorded
 * complexity, replacing the three hard-coded LIS solutions the section used to
 * show on every review. A problem with none renders no section.
 *
 * Complexity is back (LG-639, spec D19), and it is the client's own claim: the
 * `timeComplexity` / `spaceComplexity` columns LG-632's note anticipated now
 * exist on `CodingPostMortem`, filled from always-shown fields on the client's
 * post-mortem form. Time and space are separate comment targets, and a cost
 * the client did not claim renders no atom.
 *
 * Verification is back too (LG-637, spec T3/D18), reading `submission.
 * verification` — the artifact's own run/submit counts and failed tests,
 * judged against the engagement's live targets. Null when the artifact
 * records no counts, and the section is then absent rather than empty, like
 * complexity above.
 */
export function CodingReview(props: ReviewBodyProps): ReactNode {
  const { submission, getSectionRef } = props
  const { commentsFor, approachesFor, approachesErrorFor } =
    useCoachingStore().review
  const comments = commentsFor(submission.id)
  const approaches = approachesFor(submission.id)

  return (
    <>
      <RankingBanner ranking={submission.ranking} />
      <div className="flex flex-col gap-3.5">
        <CodeSection
          // The one seam that knows the difference between "this artifact
          // carried no code" and "nobody wired the prop": `code` is optional on
          // the submission and required on the section, so a body that forgets
          // it cannot compile into a section claiming the artifact was empty.
          code={submission.code ?? null}
          comments={comments}
          anchorRef={getSectionRef('code')}
        />
        <ComplexitySection
          complexity={submission.complexity}
          comments={comments}
          anchorRef={getSectionRef('complexity')}
        />
        <CodingPacingSection
          total={submission.pacing.total}
          comments={comments}
          anchorRef={getSectionRef('pacing')}
        />
        <VerificationSection
          verification={submission.verification}
          comments={comments}
          anchorRef={getSectionRef('verification')}
        />
        <AlternativeApproaches
          submissionId={submission.id}
          approaches={approaches}
          error={approachesErrorFor(submission.id)}
          comments={comments}
          anchorRef={getSectionRef('alternative')}
        />
        <PostMortemSection
          postMortem={submission.postMortem}
          comments={comments}
          anchorRef={getSectionRef('postmortem')}
        />
        <div ref={getSectionRef('overall')}>
          <OverallComment submissionId={submission.id} />
        </div>
      </div>
    </>
  )
}
